import { useMemo } from 'react';
import { useGetDatasetsQuery } from '../state/apiSlice';

/**
 * Returns the set of tags currently in use across the dataset catalog,
 * deduped case-insensitively, sorted alphabetically (locale-aware).
 *
 * Why: the previous free-text Tags TextField fragmented the catalog into
 * "telecom" / "Telecom" / "telecoms" / "telco" — same concept, four
 * surface forms. Surfacing the existing universe as autocomplete
 * suggestions nudges users toward consistent labels without forcing a
 * managed taxonomy.
 *
 * Storage policy: when two casings collide (e.g. "Telecom" and "telecom"
 * both exist on different datasets), we keep the FIRST encountered
 * casing. That's a defensible default — the alternative would be either
 * lower-casing the suggestion (loses information) or returning both
 * (defeats dedup). If a future style guide mandates a specific casing,
 * tweak the dedup rule here in one place.
 *
 * The flatten+dedupe+sort is memoised on the underlying datasets array
 * reference. RTK Query keeps that reference stable across renders until
 * a refetch lands, so this hook is essentially free in steady state.
 */
export function useTagUniverse() {
    const { data: datasets } = useGetDatasetsQuery();

    return useMemo(() => {
        const raw = datasets?.raw;
        if (!raw || raw.length === 0) return [];

        // First-casing-wins map: lowercased key → original-cased value.
        const seen = new Map();
        for (const ds of raw) {
            const tags = ds?.Tags;
            if (!Array.isArray(tags)) continue;
            for (const tag of tags) {
                if (typeof tag !== 'string') continue;
                const trimmed = tag.trim();
                if (!trimmed) continue;
                const key = trimmed.toLowerCase();
                if (!seen.has(key)) seen.set(key, trimmed);
            }
        }

        // Locale-aware, case-insensitive sort. `sensitivity: 'base'`
        // collapses casing/accents for ordering only — the displayed
        // value still preserves whatever casing won above.
        return Array.from(seen.values()).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
    }, [datasets?.raw]);
}
